import React, { Suspense } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactLoading from 'react-loading';
import { RootState } from 'handlers';
import { uploadDocuments } from 'thunks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import FormContainer from 'components/LoanForm/FormContainer';
import Button from 'components/Button';
import DropzoneWidget, { useFiles } from 'components/DropzoneWidget';
import { getApplicationData } from 'selectors/getApplicationData';
import { DigifiDocumentType } from 'api/DocumentsApi';
import { StepComponent } from 'components/Steps/stepUtils';
import { DROPBOX_EXTENSIONS } from 'components/SetupDeduction/ManualDeduction/components/VerifyPayrollDeduction/VerifyPayrollDeduction';
import { usePartnerData } from 'enums/PartnerName';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { setPaystubsUploaded } from 'handlers/applicationData';

import styles from './UploadIncomeVerification.module.scss';

const FilesPreview = React.lazy(() => import('components/FilesPreview'));

const renameFiles = (cb: (files: File[]) => void, filePrefix?: string) => (files: File[]) => {
  const prefix = filePrefix ? `${filePrefix} ` : '';
  return cb(files.map((file) => new File([file], `${prefix}${file.name}`, { type: file.type })));
};

const UploadPaystubs = ({ handleNext }: StepComponent) => {
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { handleAddFiles, files, handleRemoveFiles } = useFiles();
  const { preVerifyData } = useSelector(getApplicationData);
  const isVerifyIncomeFlow = preVerifyData?.paystubsRequired || preVerifyData?.needsVerifyIncomeOption;

  const {
    id: applicationId,
    notEmployed,
    totalAnnualIncome,
    employment: {
      employer1: { verifiedAnnualIncomeAmount },
    },
  } = useSelector(getApplicationData).application!;
  const { isApplicationStatusUpdating } = useSelector(getApplicationData);
  const { isLoading } = useSelector((state: RootState) => state.filesUpload);
  const { downloadPayrollInstructions } = usePartnerData();

  const handleContinue = async () => {
    const filesForUpload: File[] = files.map((item) => item.file);

    analytics.track('Upload Paystubs in Progress');
    await dispatchWithUnwrap(
      uploadDocuments({ applicationId, files: filesForUpload, fileType: DigifiDocumentType.Paystub }),
    );

    if (isVerifyIncomeFlow) dispatch(setPaystubsUploaded(true));

    handleNext();
  };

  const remainingIncomeToVerify = formatMonetaryAmount((totalAnnualIncome ?? 0) - (verifiedAnnualIncomeAmount ?? 0));

  const formContent = (() => {
    if (preVerifyData?.needsVerifyIncomeOption) {
      return {
        title: `Verify ${remainingIncomeToVerify} of income`,
        subtitle: `Please upload images or files that verify your additional ${remainingIncomeToVerify} of stated income.`,
      };
    }

    if (notEmployed) {
      return {
        title: 'Upload Income Verification',
        subtitle: 'Upload documents such as a W2, 1099, or tax return to help us verify your income.',
      };
    }

    return {
      title: 'Upload Paystubs',
      subtitle: 'Upload your two most recent paystubs to help us verify your income.',
    };
  })();

  return (
    <FormContainer title={formContent.title} subtitle={formContent.subtitle} className={styles.formContainer}>
      {!!downloadPayrollInstructions && (
        <>
          <p>To download your paystubs:</p>
          {downloadPayrollInstructions}
        </>
      )}

      <DropzoneWidget handleFiles={renameFiles(handleAddFiles, 'Paystub')} extensions={DROPBOX_EXTENSIONS} />
      <ul className={styles.filesRequirementsList}>
        <li>Acceptable file formats are PDF, JPG, GIF and PNG</li>
        <li>Maximum file size 10MB</li>
      </ul>
      {files.length ? (
        <Suspense fallback={<ReactLoading type="spin" color="#fff" />}>
          <FilesPreview files={files} handleRemove={handleRemoveFiles} />
        </Suspense>
      ) : (
        <></>
      )}
      <Button
        disabled={!files.length}
        className={styles.button}
        onClick={handleContinue}
        isLoading={isLoading || isApplicationStatusUpdating}
      >
        Next
      </Button>
    </FormContainer>
  );
};

export default UploadPaystubs;
